import { remove } from 'lodash-es';
import { State } from 'workflow-compiler-provider';
import { useI18n } from '@/shared/helpers/i18n';
import { app } from '@/main';
import { capitalize } from '@/shared/helpers/helpers';
import { formatXml, safeQuerySelector, safeQuerySelectors } from '@/modules/questionnaire/utils';
import { SourceItem } from '@/components/AccessPicker/types';
import PedigreeLink, { ERelationType } from './PedigreeLink';
import PedigreeMapper from './PedigreeMapper';
import PedigreeNode from './PedigreeNode';

const { t } = useI18n();

export enum EGender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  UNKNOWN = 'UNKNOWN',
}

export enum EStatus {
  // noinspection JSUnusedGlobalSymbols
  NORMAL = 'alive',
  DECEASED = 'deceased',
  STILLBIRTH = 'stillbirth',
  PREGNANCY = 'pregnancy',
  MISCARRIAGE = 'miscarriage',
  ABORTION = 'abortion',
}

export enum EMapperOperation {
  ADD = 'add',
  REMOVE = 'remove',
  NOOP = 'noop',
}

const FamilyRelations = {
  PROBAND: 'proband',
  FATHER: 'father',
  MOTHER: 'mother',
  CHILDREN: 'children',
  PARTNER: 'partner',
};

export interface ISurgery {
  surgery: string;
  surgeryDate: string;
}

export interface IMarker {
  name: string;
  testResult: 'NEGATIVE' | 'POSITIVE';
}

export interface IMetadata {
  treatment: string;
  surgeries: ISurgery[];
  markers: IMarker[];
}

export interface IClinicalInfo {
  stateType: 'alive' | 'deceased';
  diseaseName: string;
  diseaseICD10: string;
  metadata: IMetadata;
}

export interface IPedigreeGraphNode extends Object {
  id: number;
  name: string;
  isProband?: boolean;
  birthDate?: string;
  gender?: EGender;
  status?: EStatus;
  clinicalInfos?: IClinicalInfo[] | never[];
  surgeries?: ISurgery[];
  order?: number;
  origin?: Element;
  /**
   * Contains runtime-only mapping data.
   */
  metadata?: any;
}

export interface IPedigreeGraphLink {
  id: number,
  source: number,
  target: number,
  relationType: string,
  override?: boolean,
}

export interface IPedigreeMapperLinkAdd {
  operation: EMapperOperation.ADD,
  source: string,
  target: string,
  relationType: ERelationType,
  override?: 'false' | 'true',
  force?: 'false' | 'true',
}

export interface IPedigreeMapperLinkRemove {
  operation: EMapperOperation.REMOVE,
  source: string,
  target: string,
  linkTag?: string,
}

export interface IPedigreeMapperLinkNoop {
  operation: EMapperOperation.NOOP,
}

export type IPedigreeMapperLink = IPedigreeMapperLinkAdd | IPedigreeMapperLinkRemove | IPedigreeMapperLinkNoop;

export interface PedigreeGraph {
  id: number;
  nodes: IPedigreeGraphNode[];
  links: IPedigreeGraphLink[];
  createdDate: string;
  locale?: string;
}

export enum EPedigreeEntryType {
  'builder' = 'builder',
  'viewer' = 'viewer'
}

export type TPedigreeEntryType = EPedigreeEntryType.builder | EPedigreeEntryType.viewer;

export interface IConditionalPedigreeItem {
  condition: string;
  path: string;
  description: string;
  gender?: string;
  tag?: string;
  updateOnly?: boolean;
}

export interface IPedigreeEntry {
  type: TPedigreeEntryType;
  path?: string;
  description?: string;
  items?: IConditionalPedigreeItem[];
  paths?: string[];
}

export interface IPedigreeQuestion {
  key: string,
  pedigree: IPedigreeEntry,
}

export interface IPedigreeEthnicity {
  key: string;
  value: string;
}

export interface IElementAttributes {
  first_name: string; // eslint-disable-line camelcase
  last_name: string; // eslint-disable-line camelcase
  phone: string;
  id: number;
  email: string;
  pinCodeField: number;
  birthDate: string;
  address1: string;
  city: string;
  state: string;
  zip_postal_code: string; // eslint-disable-line camelcase
  country: string;
  gender: string;
  ethnicities: string;
  clinicalInfos: string;
  surgeries: string;
  status: string;
  lmp: string;
  age: number;
  tag: string;

  [index: string]: any;
}

export interface IUpdateOptions {
  deleteOffset?: number;
}

export interface IUpdateLinksResult {
  isPartnerRemoved: boolean;
}

const DEBUGGING = JSON.parse(localStorage.getItem('DEBUG_PEDIGREE_MAPPER') || 'false');

// noinspection JSMethodCanBeStatic
/**
 * Serves a pedigree structure
 *
 * @example result of the builder:
 *
 * <?xml version="1.0" encoding="UTF-8"?>
 * <root>
 *    <proband id="1">
 *       <children id="1">
 *          <male id="1" />
 *          <male id="2" />
 *          <female id="1" />
 *          <female id="2" />
 *          <female id="3" />
 *       </children>
 *       <mother id="1">
 *          <children id="1">
 *             <male id="1" />
 *          </children>
 *          <mother id="1">
 *             <children id="1">
 *                <female id="1" />
 *                <female id="2" />
 *             </children>
 *          </mother>
 *       </mother>
 *       <parents id="1">
 *          <female id="1" />
 *          <female id="2" />
 *       </parents>
 *       <father id="1">
 *          <mother id="1">
 *             <children id="1">
 *                <male id="1" />
 *                <male id="2" />
 *                <female id="1" />
 *             </children>
 *          </mother>
 *       </father>
 *       <partner/>
 *    </proband>
 * </root>
 * */
export class PedigreeServiceFactory {
  /**
   * Holds a root of the pedigree tree
   */
  private root: Document;

  private readonly ignoredElements = ['children'];

  private readonly mapper = new PedigreeMapper();

  public app!: any;

  private isLoadedResolver: any;

  public isLoaded = new Promise(resolve => {
    this.isLoadedResolver = resolve;
  });

  private static readonly initialTree = `
<root>
  <proband>
    <mother description="questionnaire.pedigree.my_mother"/>
    <father description="questionnaire.pedigree.my_father"/>
  </proband>
</root>`;

  /**
   * Class Constructor
   */
  constructor(xmlInit: string = PedigreeServiceFactory.initialTree) {
    this.root = this.fromString(xmlInit);
  }

  /**
   * Returns a singleton instance
   */
  public getInstance() {
    return this;
  }

  // noinspection JSMethodCanBeStatic
  /**
   * Returns a DOM tree from the given XML-like string
   *
   * @param string
   */
  private fromString(string: string) {
    return new DOMParser().parseFromString(string, 'text/xml');
  }

  /**
   * Retrieves an existing elements or creates new by given path in the pedigree root
   *
   * @param path
   * @param tag
   * @param updateOptions
   */
  public resolveUpdateElements(path: string, updateOptions?: IUpdateOptions, tag?: string): Element {
    return path.split('/').filter(Boolean).reduce(({
      xpath,
      parent,
    }: { xpath: string, parent: Element }, segment: string, index: number, arr: string[]) => {
      const newXpath = `${xpath}/${segment}`;
      const lastSegment = arr.length - 1 === index;

      // Query for the existing element(s) at the path, if any
      const existing = this.getElementsByPath(newXpath, tag);

      // Pull the delete option in case the number of elements (siblings) is decreased.
      // If the 'deleteOffset' equals to the number of the existing elements, then they should be all removed (if any)
      const { deleteOffset } = updateOptions || {};

      // If it's NOT the last segment (we're in the middle of the xpath), and we have already element (parent
      //    element to the last segment), there's nothing to do, proceed to the next iteration (go to the lower segment)
      //    and return the just validated segment with a new parent (the first existing element returned from a query
      //    above)
      if (!lastSegment && existing.length) {
        return {
          xpath: `${xpath}/${segment}`,
          parent: existing[0],
        };
      }

      // If deleteOffset is set, check if we have to delete elements (can be done on the last segment only)
      if (lastSegment && (((typeof deleteOffset !== 'undefined') && existing.length > deleteOffset) || deleteOffset === 0)) {
        let removedCount = 0;

        // Loop over each existing (end-segment) element
        existing.forEach((member: Element) => {
          // Skip removal if element index is less than the deletion offset
          if (Number(member.getAttribute('id')) <= Number(deleteOffset)) {
            return;
          }
          member.remove();
          removedCount += 1;

          // In case of no children on the proband's "children" node,
          if (!parent.hasChildNodes() && parent.parentElement?.nodeName?.toLowerCase() === FamilyRelations.PROBAND) {
            this.removeElements('//proband/partner');
          }
        });

        // If something has removed or there was nothing to remove, just don't proceed further
        if (!existing.length || removedCount) {
          return {
            xpath: `${xpath}/${segment}`,
            parent: existing[0],
          };
        }
      }

      // Let's create a new element with a new index, even already having an existing element
      //    unless it's a system-reserved element name
      if (!(existing.length && [FamilyRelations.MOTHER, FamilyRelations.FATHER, FamilyRelations.PROBAND].includes(segment.toLowerCase()))) {
        // Create a proband's partner, if we create her/his children...
        if (parent.nodeName.toLowerCase() === FamilyRelations.CHILDREN
          && parent.parentElement?.nodeName?.toLowerCase() === FamilyRelations.PROBAND) {
          const existingParent = this.getElementsByPath('//proband/partner');

          // ...and no parent yet
          if (!existingParent.length) {
            this.createElement(FamilyRelations.PARTNER, parent.parentElement, {
              id: 1,
              description: 'questionnaire.pedigree.my_partner',
            });
          }
        }

        //  Make initial attributes for the new element to create
        //  "id" is incremental and is primarily used for internal purposes to index elements. Index base is 1.
        //  Additionally,it can be used for displaying node names as "Son 1", "Son 2", etc.
        const attrs = lastSegment ? {
          id: existing.length ? existing.length + 1 : 1,
          tag,
        } : {};

        return {
          xpath: `${xpath}/${segment}`,
          parent: this.createElement(segment, parent, attrs),
        };
      }

      console.warn(`Unexpected scenario: Element ${segment} in the path ${xpath} cannot be created. XML dump: \n${this.toString()}`);

      return {
        xpath: `${xpath}/${segment}`,
        parent: existing[0],
      };
    }, {
      xpath: '/',
      parent: this.root.firstElementChild,
    }).parent;
  }

  /**
   * Returns elements by a given path
   *
   * @param path
   * @param tag
   * @see http://www.whitebeam.org/library/guide/TechNotes/xpathtestbed.rhtm
   */
  public getElementsByPath(path: string, tag?: string): Element[] {
    const elements: Element[] = [];
    const iterator = this.root.evaluate(path, this.root, null, XPathResult.ANY_TYPE);
    let element;

    // eslint-disable-next-line no-cond-assign
    while (element = iterator.iterateNext() as Element) {
      const elementTag = element.getAttribute('tag');

      if (!elementTag || !tag || elementTag === tag) {
        elements.push(element);
      }
    }

    return elements;
  }

  /**
   * Returns the multiple elements by a given path
   *
   * @param paths
   */
  public getElementsByPaths(paths: string[]): Element[] {
    const elements: Element[] = [];

    paths.forEach((path: string) => {
      elements.push(...this.getElementsByPath(`/${path}`));
    });

    return elements;
  }

  /**
   * Creates an element by given name on a given parent with given attributes
   *
   * @param name
   * @param parent
   * @param attrs
   */
  private createElement(name: string, parent: Element, attrs: Partial<IElementAttributes>): Element {
    const matches = this.parseLink(name);
    const element = this.root.createElement(matches.element);

    this.setElementAttributes(element, attrs);

    if (matches.id) {
      this.setElementAttributes(element, {
        id: Number(matches.id),
      });
    }

    if (matches.tag) {
      this.setElementAttributes(element, {
        tag: matches.tag,
      });
    }

    if (matches.name) {
      this.setElementAttributes(element, {
        name: matches.name,
      });
    }

    parent.appendChild(element);

    return element;
  }

  // noinspection JSMethodCanBeStatic
  /**
   * Sets a given element's value
   *
   * @param element
   * @param value
   */
  private setElementValue(element: Element, value: unknown) {
    element.textContent = JSON.stringify(value);
  }

  /**
   * Sets element attributes
   *
   * @param element
   * @param attrs
   */
  private setElementAttributes(element: Element, attrs: Partial<IElementAttributes>) {
    if (!element) {
      console.warn('member is not present');

      return this;
    }

    Object.entries(attrs).forEach(([attrKey, attrVal]) => {
      try {
        element.setAttribute(attrKey, String(attrVal));
      } catch (e) {
        console.warn('setElementAttributes fail to set', element, attrKey, attrVal, e);
      }
    });

    return this;
  }

  // noinspection JSMethodCanBeStatic
  /**
   * Gets element attributes
   *
   * @param element
   * @return object
   */
  private getElementAttributes(element: Element): { links?: string } & Partial<IElementAttributes> {
    return Array.from(element.attributes)
      .reduce((acc: Partial<IElementAttributes>, cur: Attr) => ({
        ...acc,
        [cur.name]: cur.nodeValue,
      }), {});
  }

  /**
   * Adds an element by given path with given data
   *
   * @param path
   * @param data
   * @param attrs
   */
  private addElement(path: string, data?: SourceItem, attrs: Partial<IElementAttributes> = {}): Element {
    const element = this.resolveUpdateElements(path, undefined, attrs?.tag);

    this.setElementValue(element, data);
    this.setElementAttributes(element, attrs);

    return element;
  }

  /**
   * Removes elements by given path
   *
   * @param path
   * @param deleteOffset
   * @param tag
   * @private
   */
  private removeElements(path: string, deleteOffset = 0, tag = '') {
    this.resolveUpdateElements(path, { deleteOffset }, tag);
  }

  private existingElementsCount(path: string, tag?: string): number {
    return this.getElementsByPath(path, tag)?.length || 0;
  }

  private updateElementsIds(path: string) {
    const items: Element[] = this.getElementsByPath(`/${path}`);

    items.forEach((element, index) => {
      element.setAttribute('id', String(index + 1));
    });
  }

  /**
   * If the pedigreeEntry has items, we add element to each item that has satisfied condition
   *
   * @param pedigreeEntry
   * @param data
   * @param attrs
   */
  private updateConditionalBuilder(pedigreeEntry: IPedigreeEntry, data?: SourceItem, attrs: Partial<IElementAttributes> = {}) {
    pedigreeEntry?.items?.forEach(item => {
      const notConditional = !item.condition;
      // eslint-disable-next-line no-new-func
      const shouldShowItem = notConditional || new Function('value', `return ${item.condition}`)(data?.value);
      const tag = Reflect.get(item, 'tag') ? item?.tag : attrs?.tag;
      const attributes = {
        description: item.description,
        gender: item.gender,
        tag,
      };

      if (item.updateOnly) {
        if (shouldShowItem) {
          const elements = this.getElementsByPath(`/${item.path}`, tag);

          elements.forEach(element => {
            this.setElementAttributes(element, attributes);
          });
        }

        return;
      }

      if (shouldShowItem) {
        this.addElement(item.path, data, attributes);
      } else {
        this.removeElements(item.path, 0, tag);
      }
    });
  }

  /**
   * Updates the Tree by a given path and data
   *
   * @param pedigreeEntry
   * @param data
   * @param attrs
   */
  public update(pedigreeEntry: IPedigreeEntry, data?: SourceItem, attrs: Partial<IElementAttributes> = {}) {
    // add nodes for conditional builder
    const isConditionalBuilder = !!pedigreeEntry?.items;

    if (isConditionalBuilder) {
      this.updateConditionalBuilder(pedigreeEntry, data, attrs);

      return;
    }

    // add nodes for numeric builder
    const path = pedigreeEntry?.path || '';
    const existingCount = this.existingElementsCount(`/${path}`, attrs?.tag);
    const updatedCount = data?.value ? Number(data.value) : 0;
    const targetCount = updatedCount - existingCount;

    if (targetCount === 0) {
      return;
    }

    if (targetCount < 0) {
      this.removeElements(path, updatedCount, attrs?.tag);
    }

    if (targetCount > 0) {
      for (let i = 0; i < targetCount; i += 1) {
        this.addElement(path, data, attrs);
      }
    }

    // element id is used to index items with the same path
    // for paths that can be created from different questions we need to align the indexing
    this.updateElementsIds(path);
  }

  /**
   * Performs mapping logic against the state to the XML tree
   */
  public async updateMapping() {
    if (!this.app) {
      return;
    }

    const { state, configs } = this.app.$workflowManager.compiler.getGlobals();

    if (!configs.globalProps.pedigreeMapping) {
      return;
    }

    try {
      await this.clearMapping(state);
      await this.mapData(state);
    } catch (e) {
      console.error('Pedigree Mapping failed with', e);
    }
  }

  private async mapData(state: State) {
    const mappedData = await this.mapper.getMapped(state);

    mappedData.forEach(data => {
      let [member] = this.getElementsByPath(`/${data.path}`);

      if (!member) {
        member = this.resolveUpdateElements(data.path);

        this.setElementAttributes(member, {
          'mapping-clearable': true,
        });
      }

      (Object.keys(data).filter(k => k !== 'path') as (keyof IPedigreeGraphNode)[])
        .forEach(key => {
          let raw = (data as IPedigreeGraphNode)?.[key];

          if (['clinicalInfos', 'surgeries'].includes(key)) {
            const metaSerialized = member.getAttribute(key) || '[]';

            try {
              const meta = JSON.parse(metaSerialized);

              if (Array.isArray(raw)) {
                if (key === 'clinicalInfos') {
                  meta.forEach((m: any) => {
                    m.diseaseName = t(m.diseaseName);
                  });
                }
                (raw as (IClinicalInfo | ISurgery)[]).push(...meta);
              } else {
                raw = meta;
              }
            } catch (__) {
              //
            }
          }

          const serialized = typeof raw === 'string' ? raw : JSON.stringify(raw);

          member.setAttribute(key, serialized);
        });
    });
  }

  private async clearMapping(state: State) {
    const mapped = await this.mapper.getMapped(state);

    // Clean old data
    mapped.forEach(data => {
      this.getElementsByPath(`/${data.path}`).forEach(member => {
        (Object.keys(data).filter(k => k !== 'path') as (keyof IPedigreeGraphNode)[]).forEach(key => {
          member.removeAttribute(key);
        });
      });
    });

    // Clean everything created by a mapper
    this.getFlatFamily()
      .forEach(member => {
        member.removeAttribute('nodeId');

        if (member.getAttribute('mapping-clearable')) {
          member.remove();
        }
      });
  }

  /**
   * Returns a serialized Tree as a string
   */
  public toString(printToConsole = false) {
    const xmlString = formatXml((new XMLSerializer()).serializeToString(this.root));

    if (printToConsole) {
      console.log((xmlString || '').replace(/&quot;/g, ' '));

      return '';
    }

    return xmlString;
  }

  /**
   * Returns a proband element
   */
  public getProband() {
    // noinspection CssInvalidHtmlTagReference
    return this.root.querySelector('proband');
  }

  /**
   * Generates and returns a pedigree XML in "Pedigree JSON" format
   */
  public toPedigreeJSON(): Omit<PedigreeGraph, 'id'> {
    const nodes: IPedigreeGraphNode[] = [];
    const links: IPedigreeGraphLink[] = [];
    const createdDate = new Date();

    createdDate.setMilliseconds(0);

    const pedigree: Omit<PedigreeGraph, 'id'> = {
      nodes,
      links,
      createdDate: createdDate.toISOString(),
      locale: this.globalState.locale,
    };

    if (!this.app) {
      return pedigree;
    }

    const proband = this.getProband();

    this.updatePersonalDetails(proband);

    this.updatePedigree(pedigree, proband);

    this.addSpecialNodesToPedigreeJSON(pedigree);

    this.cleanupPedigreeJSON(pedigree);

    if (DEBUGGING) {
      this.toString(true);
    }

    // Normalization
    // Drop internal keys
    pedigree.links.forEach(l => {
      delete l.override;
    });

    pedigree.nodes.forEach(n => {
      delete n.origin;
      n.name = n.name.replace(/&rsquo;/g, '\'');
    });

    return pedigree;
  }

  // Returns next available (incremental) ID of links of nodes
  private getNextId(pedigree: Omit<PedigreeGraph, 'id'>, type: 'nodes' | 'links'): number {
    if (type === 'nodes') {
      return (pedigree.nodes.reduce((acc, cur) => (acc > (cur.id || 0) ? acc : (cur.id || 0)), 0)) + 1;
    }

    return (pedigree.links.reduce((acc, cur) => (acc > (cur.id || 0) ? acc : (cur.id || 0)), 0)) + 1;
  }

  // Calculates and returns Gender based of the element characteristics
  private guessGender(person: string): EGender

  private guessGender(person: Element): EGender

  private guessGender(person: Element | string): EGender {
    const nodeName = typeof person === 'string' ? person : person.nodeName;
    let gender: EGender;

    if (nodeName === FamilyRelations.FATHER || nodeName === EGender.MALE.toLowerCase()) {
      gender = EGender.MALE;
    } else if (nodeName === FamilyRelations.MOTHER || nodeName === EGender.FEMALE.toLowerCase()) {
      gender = EGender.FEMALE;
    } else if (nodeName === EGender.UNKNOWN.toLowerCase()) {
      gender = EGender.UNKNOWN;
    } else if (nodeName === FamilyRelations.PROBAND && typeof person !== 'string') {
      gender = person.getAttribute('gender') as EGender || EGender.FEMALE;
    } else if (nodeName === FamilyRelations.PARTNER && typeof person !== 'string') {
      gender = person.getAttribute('gender') as EGender || EGender.MALE;
    } else if (typeof person !== 'string') {
      gender = person.getAttribute('gender') as EGender;
    } else {
      gender = person as EGender;
    }

    if (!gender) {
      throw new Error(`Unknown gender for node ${nodeName}`);
    }

    return gender;
  }

  private get globalState(): any {
    return this?.app?.$workflowManager?.compiler?.getState || {};
  }

  /**
   * add Special nodes ToPedigree JSON (for example: sperm_donor. egg_donor. exc)
   */
  private addSpecialNodesToPedigreeJSON(pedigree: Omit<PedigreeGraph, 'id'>) {
    const ignoreNodeNames = [
      FamilyRelations.FATHER,
      FamilyRelations.MOTHER,
      FamilyRelations.CHILDREN,
      FamilyRelations.PARTNER,
      EGender.UNKNOWN.toLowerCase(),
      EGender.MALE.toLowerCase(),
      EGender.FEMALE.toLowerCase(),
    ];

    this.getFlatFamily()
      .filter(nodeData => !ignoreNodeNames.includes(nodeData?.nodeName))
      .forEach(nodeData => this.updatePedigree(pedigree, nodeData));
  }

  cleanupPedigreeJSON(pedigree: Omit<PedigreeGraph, 'id'>) {
    pedigree.nodes.forEach(node => delete node.metadata);
  }

  // Mutates (by-ref) Pedigree JSON structure by adding compiled pedigree nodes
  private updatePedigree(pedigree: Omit<PedigreeGraph, 'id'>, person: Element, extFatherId?: number, existingPersonId?: number | null): number {
    let node = pedigree.nodes.find(n => person && n.id && n.id === Number(person.getAttribute('nodeId'))) as PedigreeNode | null;
    const partners = safeQuerySelectors(person, ':scope > partner');
    let personId: number;

    if (person.parentElement?.nodeName?.toLowerCase() === 'children') {
      console.warn('updatePedigree should not be called on a child node, skipping');

      return -1;
    }

    if (existingPersonId) {
      personId = existingPersonId;
    } else if (node) {
      personId = node.id;
    } else {
      personId = this.getNextId(pedigree, 'nodes');

      node = new PedigreeNode()
        .setId(personId)
        .setName(this.guessName(person))
        .setGender(this.guessGender(person));

      person.setAttribute('nodeId', String(personId));

      const personData = this.getElementAttributes(person);

      this.setPersonData(personData, node, partners[0], person);

      pedigree.nodes.push(node);
    }

    this.updateParents(person, pedigree, personId);

    partners.forEach(partner => {
      const partnerId = this.updatePedigree(pedigree, partner);

      const serializedLinks = partner.getAttribute('links') || '[]';
      const links = JSON.parse(serializedLinks) as IPedigreeMapperLink[];

      if (links.length) {
        const updateLinksResult = this.updateLinks(links, pedigree, partner);

        this.mapChildren(person, pedigree, extFatherId, personId, updateLinksResult.isPartnerRemoved ? undefined : partnerId);
      } else {
        this.addLink(pedigree, personId, partnerId, ERelationType.PARTNER);
        this.mapChildren(person, pedigree, extFatherId, personId, partnerId);
      }
    });

    if (!partners.length) {
      this.mapChildren(person, pedigree, extFatherId, personId);
    }

    return personId;
  }

  private updateParents(person: Element, pedigree: Omit<PedigreeGraph, 'id'>, personId: number) {
    let father = safeQuerySelector(person, ':scope > father');
    const mother = safeQuerySelector(person, ':scope > mother');
    const existingParents = pedigree.links
      .filter(l => l.relationType === ERelationType.PARENT && l.target === personId)
      .map(l => pedigree.nodes.find(n => n.id === l.source));

    let fatherId: number | null = existingParents.find(ep => ep.gender === EGender.MALE)?.id || null;
    let motherId: number | null = existingParents.find(ep => ep.gender === EGender.FEMALE)?.id || null;

    // Compensate missing father from the config
    if (mother && !father && !fatherId) {
      const motherPath = this.getXPath(mother);
      const missingFatherPath = `${motherPath.split('/').slice(0, -1).join('/')}/father`;

      father = this.resolveUpdateElements(missingFatherPath);
    }

    if (father) {
      fatherId = this.updatePedigree(pedigree, father, undefined, fatherId);

      this.addLink(pedigree, fatherId, personId, ERelationType.PARENT);
    }

    if (mother) {
      motherId = this.updatePedigree(pedigree, mother, fatherId || undefined, motherId);
      this.addLink(pedigree, motherId, personId, ERelationType.PARENT);
    }

    if (fatherId && motherId) {
      this.addLink(pedigree, fatherId, motherId, ERelationType.PARTNER);
    }
  }

  private mapChildren(person: Element, pedigree: Omit<PedigreeGraph, 'id'>, extFatherId: number | undefined, personId: number, partnerId?: number) {
    safeQuerySelectors(person, ':scope > children > *').forEach(child => {
      const serializedLinks = child.getAttribute('links') || '[]';
      const links = JSON.parse(serializedLinks) as IPedigreeMapperLink[];

      this.updateLinks(links, pedigree, child);

      this.resolveMapperNode(child, pedigree);
      const childId = Number(child.getAttribute('nodeId'));

      if (extFatherId) {
        this.addLink(pedigree, extFatherId, childId, ERelationType.PARENT);
      }

      this.addLink(pedigree, personId, childId, ERelationType.PARENT);

      if (partnerId) {
        const partnerNode = pedigree.nodes[partnerId - 1];
        const partnerRelationType = partnerNode.metadata?.relationType || ERelationType.PARENT;

        this.addLink(pedigree, partnerId, childId, partnerRelationType);
      }

      const node = pedigree.nodes.find(n => n.id === childId) as PedigreeNode;
      const personData = this.getElementAttributes(child);

      this.setPersonData(personData, node, null);
    });
  }

  // Calculates and returns printable Name based of the element characteristics
  private guessName(person: Element): string

  private guessName(person: string, id?: string): string

  private guessName(person: Element | string, id?: string): string {
    if (typeof person === 'string') {
      const nodeNameC = capitalize(person);

      return id ? `${nodeNameC} ${id}` : nodeNameC;
    }

    const nodeName = capitalize(person.nodeName);

    if (person.getAttribute('description')) {
      return t(person.getAttribute('description') as string) as string;
    }
    if (person.getAttribute('name')) {
      return person.getAttribute('name') as string;
    }
    if (person.getAttribute('id')) {
      return `${nodeName} ${person.getAttribute('id')}`;
    }

    return nodeName;
  }

  private updatePersonalDetails(proband: Element) {
    const { state } = this.app.$workflowManager.compiler.getGlobals();
    const personalDetails: Record<string, string> = Object.entries(state?.data?.['personal-details']).reduce((acc, [key, value]) => {
      let personalInfoKey = key.replace('personal_details_', '');

      if (personalInfoKey === 'id' && key === 'personal_details_id') {
        personalInfoKey = 'identityNumber';
      }

      return {
        ...acc,
        [personalInfoKey]: value,
      };
    }, {});

    if (personalDetails) {
      this.setElementAttributes(proband, personalDetails);
    }
  }

  // noinspection JSMethodCanBeStatic
  private setPersonData(personData: Partial<IElementAttributes>, node: PedigreeNode, partner: Element | null, person?: Element) {
    if (personData.birthDate) {
      node.setBirthDate(personData.birthDate);
    }

    if (personData.gender) {
      node.setGender(personData.gender as EGender);

      if (partner) {
        partner.setAttribute('gender', personData.gender === EGender.MALE ? EGender.FEMALE : EGender.MALE);
      }
    }

    if (personData.first_name || personData.last_name) {
      node.setName(`${personData.first_name} ${personData.last_name}`);
    }

    if (person && person.tagName.toLowerCase() === FamilyRelations.PROBAND) {
      node.makeProband();
    }

    if (personData.ethnicities) {
      node.setEthnicities(personData.ethnicities);
    }

    if (personData.clinicalInfos) {
      const processedClinicalInfos = (JSON.parse(personData.clinicalInfos) || []).map((entry: IClinicalInfo) => ({
        ...entry,
        diseaseName: t(entry.diseaseName),
      }));

      node.setClinicalInfos(JSON.stringify(processedClinicalInfos));
    }

    if (personData.surgeries) {
      node.setSurgeries(personData.surgeries);
    }

    if (personData.status) {
      node.setStatus(personData.status as EStatus);
    }

    if (personData.lmp) {
      node.setLmp(personData.lmp);
    }

    if (personData.relationType) {
      node.setRelationType(personData.relationType);
    }
  }

  private resolveMapperNode(path: string, pedigree: Omit<PedigreeGraph, 'id'>): IPedigreeGraphNode

  private resolveMapperNode(element: Element, pedigree: Omit<PedigreeGraph, 'id'>): IPedigreeGraphNode

  private resolveMapperNode(path: string | Element, pedigree: Omit<PedigreeGraph, 'id'>): IPedigreeGraphNode {
    let element: Element;
    let name = '';
    let gender: EGender | null = null;

    if (typeof path === 'string') {
      [element] = this.getElementsByPath(`/${path}`);

      if (!element) {
        element = this.resolveUpdateElements(`/${path}`);

        const parsed = this.parseLink(path);

        name = parsed.name || this.guessName(parsed?.element, parsed?.id);
        gender = this.guessGender(parsed?.element);
      }
    } else {
      element = path;
    }

    name = name || this.guessName(element);
    gender = gender || this.guessGender(element);

    let node = pedigree.nodes.find(n => element && n.id === Number(element.getAttribute('nodeId')));
    const status = element.getAttribute('status');

    if (!node) {
      const personId = this.getNextId(pedigree, 'nodes');

      node = new PedigreeNode()
        .setId(personId)
        .setName(name)
        .setGender(gender);

      if (status) {
        (node as PedigreeNode).setStatus(status as EStatus);
      }

      element.setAttribute('nodeId', String(personId)); // Rewrite mapper-define ID with a calculated one

      pedigree.nodes.push(node);
    }

    return node;
  }

  private updateLinks(links: IPedigreeMapperLink[], pedigree: Omit<PedigreeGraph, 'id'>, person: Element): IUpdateLinksResult {
    const result: IUpdateLinksResult = {
      isPartnerRemoved: false,
    };

    links.forEach(link => {
      // NOOP
      if (link.operation === EMapperOperation.NOOP) {
        return;
      }

      // ADD
      if (link.operation === EMapperOperation.ADD) {
        const source = link.source === 'self'
          ? this.resolveMapperNode(person, pedigree)
          : this.resolveMapperNode(link.source, pedigree);
        const target = link.target === 'self'
          ? this.resolveMapperNode(person, pedigree)
          : this.resolveMapperNode(link.target, pedigree);

        this.addLink(pedigree, source.id, target.id, link.relationType, JSON.parse(link.override || 'false'), JSON.parse(link.force || 'false'));

        return;
      }

      // REMOVE
      if (link.operation === EMapperOperation.REMOVE) {
        let source: IPedigreeGraphNode | null = null;
        let target: IPedigreeGraphNode | null = null;

        if (link.source) {
          source = link.source === 'self'
            ? this.resolveMapperNode(person, pedigree)
            : this.resolveMapperNode(link.source, pedigree);
        }

        if (link.target) {
          target = link.target === 'self'
            ? this.resolveMapperNode(person, pedigree)
            : this.resolveMapperNode(link.source, pedigree);
        }

        if (target) {
          const isPartnerRemoved = pedigree.links.some(l => source && l.source === source.id && l.target === target.id && l.relationType === ERelationType.PARTNER);

          remove(pedigree.links, l => source && l.source === source.id && l.target === target.id);
          result.isPartnerRemoved = isPartnerRemoved || link.linkTag === ERelationType.PARTNER;
        } else {
          const isPartnerRemoved = pedigree.links.some(l => source && l.source === source.id && l.relationType === ERelationType.PARTNER);

          remove(pedigree.links, l => source && l.source === source.id);
          result.isPartnerRemoved = isPartnerRemoved || link.linkTag === ERelationType.PARTNER;
        }
      }
    });

    return result;
  }

  public addLink(pedigree: Omit<PedigreeGraph, 'id'>, sourceId: number, targetId: number, relationType: ERelationType, override = false, force = false): void {
    const valid = pedigree.nodes.some(n => n.id === sourceId) && pedigree.nodes.some(n => n.id === targetId);

    if (!valid) {
      console.warn(`Invalid link: ${sourceId}-->${targetId}. There are no nodes with such source and/or target in the graph.`);

      return;
    }

    if (override) {
      // Remove existing links to target (if any), and add the overridden one (the last wins)
      remove(pedigree.links, l => l.relationType === relationType && l.target === targetId);
    } else {
      const isOverridden = pedigree.links.some(l => l.relationType === relationType && l.target === targetId && l.override);

      // We already have an overridden link, stop further actions
      if (isOverridden) {
        return;
      }
    }

    const existing = pedigree.links.find(pl => pl.source === sourceId && pl.target === targetId) as PedigreeLink | null;

    if (existing) {
      return;
    }

    const PARENT_RELATION_TYPES = [ERelationType.PARENT, ERelationType.SPERM_DONOR];

    if (PARENT_RELATION_TYPES.includes(relationType)) {
      const targetParents = pedigree.links.filter(pl => PARENT_RELATION_TYPES.includes(pl.relationType as ERelationType) && pl.target === targetId) as PedigreeLink[];

      if (targetParents.length === 2 && !force) {
        return;
      }
    }

    const link = new PedigreeLink(
      this.getNextId(pedigree, 'links'),
      sourceId,
      targetId,
    );

    pedigree.links.push(link);

    link.setRelationType(relationType).setOverride(override);
  }

  /**
   * Returns a flatted tree
   */
  public getFlatFamily() {
    return Array.from(this.root.querySelectorAll('*')).slice(2).filter(node => !this.ignoredElements.includes(node.nodeName));
  }

  /**
   * Returns count of family members
   */
  public getMembersCount(): number {
    return this.getFlatFamily().length;
  }

  private getQuestionsWithPedigreeBuilder(obj: object): Array<IPedigreeQuestion> {
    if (!obj || typeof obj !== 'object') {
      return [];
    }

    return Object.values(obj).reduce(
      (foundItems: Array<any>, entryValue: any) => {
        const pedigree = entryValue?.component?.props?.pedigree;

        if (pedigree?.type === EPedigreeEntryType.builder) {
          foundItems.push({
            key: entryValue?.key,
            pedigree,
          });
        } else if (typeof entryValue === 'object') {
          foundItems = foundItems.concat(this.getQuestionsWithPedigreeBuilder(entryValue));
        }

        return foundItems;
      },
      [],
    );
  }

  /**
   * Builds a pedigree structure based on Vue app
   *
   * @param vm
   * @param initialTree
   * @param pedigreeMapping
   */
  public init(vm: any, initialTree = PedigreeServiceFactory.initialTree, pedigreeMapping: any = null): this {
    this.app = app.config?.globalProperties || vm;
    this.app.$pedigreeService = this;

    const { $workflowManager } = this.app;
    const { state, configs } = $workflowManager.compiler.getGlobals();

    if (pedigreeMapping) {
      this.mapper.configure(pedigreeMapping as string);
      this.isLoadedResolver();
    }

    const familyState = state?.data?.questionnaire?.family;
    const pregnancyState = state?.data?.questionnaire?.pregnancy;

    this.root = this.fromString(initialTree);

    if (!familyState && !pregnancyState) {
      return this;
    }

    const familyAndPregnancyState = {
      ...(familyState || {}),
      ...(pregnancyState || {}),
    };

    const questionsWithPedigreeBuilder: Array<IPedigreeQuestion> = this.getQuestionsWithPedigreeBuilder(configs.pages);

    questionsWithPedigreeBuilder.forEach((pedigreeQuestion: IPedigreeQuestion) => {
      const { key, pedigree } = pedigreeQuestion;
      const questionsKeys = Object.keys(familyAndPregnancyState).filter(stateKey => stateKey.startsWith(key));

      if (!questionsKeys) {
        return;
      }

      questionsKeys.forEach(questionKey => {
        const storeValue = familyAndPregnancyState[questionKey] as SourceItem;

        this.update(pedigree, storeValue, { description: pedigree.description, tag: questionKey });
      });
    });

    return this;
  }

  /**
   * Returns element by given XPath
   *
   * @param e
   * @param payload
   */
  public getXPath(e: Element, payload: string = e.nodeName.toLowerCase()): string {
    const parent = e.parentElement;

    return parent ? this.getXPath(parent, `${parent.nodeName.toLowerCase()}/${payload}`) : payload;
  }

  private parseLink(link: string) {
    return link.match(
      /(?<path>(?:.*\/)?(?<element>[^[]+))(?:\[@id='(?<id>[a-z\d_-]+)'])?(?:\[@tag='(?<tag>[a-z\d_-]+)'])?(?:\[@name='(?<name>[a-z\d &;_-]+)'])?/i,
    )?.groups as { tag?: string, path: string, element: string, id?: string, name?: string };
  }
}

const pedigreeService = new PedigreeServiceFactory();

export {
  pedigreeService,
};
